import { useMutation, useQuery } from '@zg-rentals/react-query5';
import { useDispatch } from 'react-redux';

import CurrentProperty from '../../model/CurrentProperty';

import postingUtils from '../../utils/postingUtils';
import makeRequest from '../../utils/makeRequestUtils';
import useRequestConfig from '../useRequestConfig';
import DeliveryMethod from '../../model/DeliveryMethod';
import PropertiesActions from '../../actions/PropertiesActions';
import { RENTAL_MANAGER_API_V1_PREFIX, RENTAL_MANAGER_API_V2_PREFIX } from '../../constants/RouteConstants';
import { logError } from '@zg-rentals/log-error';

const useGetDeliveryMethod = (propertyId, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/deliveryMethod`,
      requestConfig,
      config: { params: { propertyId }, timeout: 5000 },
    });

  const queryHook = useQuery({
    queryKey: ['deliveryMethod', propertyId],
    queryFn: async () => {
      const data = await queryFn();
      return DeliveryMethod(data);
    },
    staleTime: 10 * 1000,
    ...queryOptions,
  });

  return queryHook;
};

const useEditProperties = (mutationOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = (data) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/${data.fieldName}`,
      requestConfig,
      config: {
        params: { propertyId: data.listingAlias, [data.fieldName]: data.value },
      },
    });

  return useMutation({ mutationFn: (data) => queryFn(data), ...mutationOptions });
};

const useGetListingDetails = (listingAlias, queryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = ({ signal }) =>
    makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/details`,
      requestConfig,
      config: {
        signal,
        params: {
          propertyId: listingAlias,
          includeTopDupesDetails: true,
          includeVrModels: false,
          includeHasVrModels: false,
          includeWebAnalytics: true,
          includeInappropriatePhotosTag: true,
          includeDeliveryMethod: true,
        },
      },
    });

  const queryHook = useQuery({
    queryKey: ['listingDetails', listingAlias],
    queryFn: async ({ signal }) => {
      try {
        const data = await queryFn({ signal });

        // do we need to set propertyId ?

        const freemiumData = data?.freemium || {};
        const mergedListingAndTransformedData = {
          ...postingUtils.transformDatafromApi(data),
          freemium: freemiumData,
        };

        /**
         * there is an issue where CurrentProperty.rentalProtection gets reset.
         * this is because we merge all of the properties back into CurrentProperty
         *
         * a fix on the FE is to remove the rentGuarantee key from the api response
         * and set the value onto the rentalProtection key
         *
         * the long term solution would be for the BE to return rentalProtection key from the api response
         */
        mergedListingAndTransformedData.rentalProtection = mergedListingAndTransformedData.rentGuarantee;

        delete mergedListingAndTransformedData.rentGuarantee;

        const currentPropertyModel = CurrentProperty(mergedListingAndTransformedData);

        dispatch(PropertiesActions.updateCurrentProperty(currentPropertyModel));

        return mergedListingAndTransformedData;
      } catch (error) {
        logError({
          error,
          context: {
            details: 'properties.useGetListingDetails',
            listingAlias,
          },
        });
        throw error;
      }
    },
    staleTime: 10 * 1000,
    ...queryOptions,
    enabled: Boolean(listingAlias),
  });

  return queryHook;
};
const syncCurrentPropertyRestrictions = ({ data, dispatch }) => {
  dispatch(PropertiesActions.updateCurrentPropertyRestrictions(data));
};

const cancelQueriesApplicationsSummary = ({ queryClient, listingAlias }) => {
  queryClient.cancelQueries({ queryKey: ['listingDetails', listingAlias] });
};

const useUpdateArchiveStatus = (propertyId, isArchived, queryOptions) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () => {
    return makeRequest({
      dispatch,
      endpoint: `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/${isArchived ? 'dearchive' : 'archive'}`,
      requestConfig,
      config: { params: { propertyId } },
    });
  };

  return useMutation({ mutationFn: (data) => queryFn(data), ...queryOptions });
};

export {
  cancelQueriesApplicationsSummary,
  syncCurrentPropertyRestrictions,
  useEditProperties,
  useGetDeliveryMethod,
  useGetListingDetails,
  useUpdateArchiveStatus,
};
